// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {
	batchRaidPrune,
	getAntiRaidStatus,
	getRecentJoins,
	togglePanicLockdown,
} from '@app/features/guild/commands/GuildAntiRaidCommands';
import styles from '@app/features/guild/components/modals/AntiRaidPanicModal.module.css';
import {Checkbox} from '@app/features/ui/checkbox/Checkbox';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {BaseAvatar} from '@app/features/ui/components/BaseAvatar';
import {Spinner} from '@app/features/ui/components/Spinner';
import {getDefaultAvatarURLForIndex, getUserAvatarURL} from '@app/features/user/utils/AvatarUtils';
import type {
	GuildAntiRaidStatusResponse,
	RecentJoinMember,
	RecentJoinsResponse,
} from '@fluxer/schema/src/domains/guild/GuildAntiRaidSchemas';
import {Trans} from '@lingui/react/macro';
import {
	ArrowClockwiseIcon,
	ProhibitIcon,
	ShieldCheckIcon,
	ShieldWarningIcon,
	UserMinusIcon,
	WarningCircleIcon,
	WarningIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

interface AntiRaidPanicModalProps {
	guildId: string;
}

export const AntiRaidPanicModal: React.FC<AntiRaidPanicModalProps> = observer(({guildId}) => {
	const [status, setStatus] = useState<GuildAntiRaidStatusResponse | null>(null);
	const [recentJoins, setRecentJoins] = useState<RecentJoinsResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isTogglingLockdown, setIsTogglingLockdown] = useState(false);
	const [isPruning, setIsPruning] = useState(false);

	const [withinMinutes, setWithinMinutes] = useState(30);
	const [suspiciousOnly, setSuspiciousOnly] = useState(false);
	const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
	const [deleteMessageSeconds, setDeleteMessageSeconds] = useState(86400);

	const fetchAll = useCallback(
		async (mins = withinMinutes) => {
			try {
				const [statusRes, joinsRes] = await Promise.all([
					getAntiRaidStatus(guildId),
					getRecentJoins(guildId, mins),
				]);
				setStatus(statusRes);
				setRecentJoins(joinsRes);
			} catch (err: any) {
				ToastCommands.createToast({
					type: 'error',
					children: err?.message || <Trans>Failed to load anti-raid information</Trans>,
				});
			} finally {
				setIsLoading(false);
			}
		},
		[guildId, withinMinutes],
	);

	useEffect(() => {
		void fetchAll();
	}, [fetchAll]);

	const handleTimeChange = (mins: number) => {
		setWithinMinutes(mins);
		setSelectedUserIds(new Set());
		void fetchAll(mins);
	};

	const filteredMembers = useMemo(() => {
		if (!recentJoins?.members) return [];
		if (suspiciousOnly) {
			return recentJoins.members.filter((m) => m.is_suspicious);
		}
		return recentJoins.members;
	}, [recentJoins, suspiciousOnly]);

	const handleTogglePanic = async () => {
		if (!status) return;
		const nextActive = !status.is_lockdown_active;

		if (nextActive) {
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={<Trans>Activate Emergency Panic Lockdown?</Trans>}
						description={
							<Trans>
								This will freeze all invite links to this server immediately and flag raid mitigation. New
								members will not be able to join until lockdown is lifted. An automated announcement will be
								posted to keep your members informed.
							</Trans>
						}
						primaryText={<Trans>Activate Lockdown</Trans>}
						primaryVariant="danger"
						secondaryText={<Trans>Cancel</Trans>}
						onPrimary={async () => {
							setIsTogglingLockdown(true);
							try {
								const res = await togglePanicLockdown(guildId, {active: true, post_announcement: true});
								setStatus(res);
								ToastCommands.createToast({
									type: 'error',
									children: <Trans>🚨 Emergency Lockdown Activated! All server invites frozen.</Trans>,
								});
								void fetchAll();
							} catch (e: any) {
								ToastCommands.createToast({
									type: 'error',
									children: e?.message || <Trans>Failed to activate panic lockdown</Trans>,
								});
							} finally {
								setIsTogglingLockdown(false);
							}
						}}
						data-flx="guild.anti-raid-panic-modal.confirm-activate"
					/>
				)),
			);
		} else {
			setIsTogglingLockdown(true);
			try {
				const res = await togglePanicLockdown(guildId, {active: false, post_announcement: true});
				setStatus(res);
				ToastCommands.createToast({
					type: 'success',
					children: <Trans>Lockdown lifted! Invites have been restored.</Trans>,
				});
				void fetchAll();
			} catch (e: any) {
				ToastCommands.createToast({
					type: 'error',
					children: e?.message || <Trans>Failed to lift panic lockdown</Trans>,
				});
			} finally {
				setIsTogglingLockdown(false);
			}
		}
	};

	const handleSelectAllSuspicious = () => {
		if (!recentJoins?.members) return;
		const suspicious = recentJoins.members.filter((m) => m.is_suspicious).map((m) => m.user_id);
		setSelectedUserIds(new Set(suspicious));
	};

	const handleSelectAll = () => {
		if (!filteredMembers) return;
		setSelectedUserIds(new Set(filteredMembers.map((m) => m.user_id)));
	};

	const handleClearSelection = () => {
		setSelectedUserIds(new Set());
	};

	const handleToggleMember = (userId: string) => {
		setSelectedUserIds((prev) => {
			const next = new Set(prev);
			if (next.has(userId)) next.delete(userId);
			else next.add(userId);
			return next;
		});
	};

	const handleExecutePrune = (action: 'ban' | 'kick') => {
		if (selectedUserIds.size === 0) return;
		const count = selectedUserIds.size;

		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={
						action === 'ban' ? (
							<>
								<Trans>Batch Ban</Trans> {count} <Trans>Raiders?</Trans>
							</>
						) : (
							<>
								<Trans>Batch Kick</Trans> {count} <Trans>Members?</Trans>
							</>
						)
					}
					description={
						action === 'ban' ? (
							<Trans>
								This will permanently ban all selected accounts and purge their messages according to your
								selection. This action is recorded in the Server Audit Log.
							</Trans>
						) : (
							<Trans>
								This will kick all selected accounts from the server. They will be able to rejoin once invites
								are restored.
							</Trans>
						)
					}
					primaryText={
						action === 'ban' ? (
							<>
								<Trans>Ban</Trans> {count} <Trans>Accounts</Trans>
							</>
						) : (
							<>
								<Trans>Kick</Trans> {count} <Trans>Accounts</Trans>
							</>
						)
					}
					primaryVariant={action === 'ban' ? 'danger' : 'primary'}
					secondaryText={<Trans>Cancel</Trans>}
					onPrimary={async () => {
						setIsPruning(true);
						try {
							const res = await batchRaidPrune(guildId, {
								user_ids: Array.from(selectedUserIds),
								action,
								delete_message_seconds: deleteMessageSeconds,
								reason: `Anti-Raid Panic Prune: ${count} accounts`,
							});
							ToastCommands.createToast({
								type: 'success',
								children: (
									<>
										<Trans>Successfully pruned</Trans> {res.pruned_count} <Trans>raiders!</Trans>
									</>
								),
							});
							setSelectedUserIds(new Set());
							void fetchAll();
						} catch (e: any) {
							ToastCommands.createToast({
								type: 'error',
								children: e?.message || <Trans>Failed to prune raid accounts</Trans>,
							});
						} finally {
							setIsPruning(false);
						}
					}}
					data-flx="guild.anti-raid-panic-modal.confirm-prune"
				/>
			)),
		);
	};

	const isLockdown = status?.is_lockdown_active ?? false;
	const selectedCount = selectedUserIds.size;

	return (
		<Modal.Root size="large" centered data-flx="guild.anti-raid-panic-modal.root">
			<Modal.Header
				title={<Trans>Anti-Raid Panic Shield</Trans>}
				data-flx="guild.anti-raid-panic-modal.header"
			/>
			<Modal.Content data-flx="guild.anti-raid-panic-modal.content">
				<Modal.ContentLayout data-flx="guild.anti-raid-panic-modal.layout">
					<div className={styles.container}>
						{isLoading ? (
							<div className={styles.emptyState}>
								<Spinner size="large" />
								<span><Trans>Analyzing community security telemetry...</Trans></span>
							</div>
						) : (
							<>
								{/* Emergency Status Banner */}
								<div
									className={clsx(styles.statusBanner, isLockdown && styles.statusBannerActive)}
									data-flx="guild.anti-raid-panic-modal.status-banner"
								>
									<div className={styles.statusLeft}>
										<div
											className={clsx(
												styles.statusIconWrapper,
												isLockdown && styles.statusIconWrapperActive,
											)}
										>
											{isLockdown ? (
												<ShieldWarningIcon size={26} weight="fill" />
											) : (
												<ShieldCheckIcon size={26} weight="fill" />
											)}
										</div>
										<div className={styles.statusTextGroup}>
											<span className={clsx(styles.statusTitle, isLockdown && styles.statusTitleActive)}>
												{isLockdown ? (
													<Trans>🚨 EMERGENCY PANIC LOCKDOWN ACTIVE</Trans>
												) : (
													<Trans>Protection Status: Normal & Monitoring</Trans>
												)}
											</span>
											<span className={styles.statusSubtitle}>
												{isLockdown ? (
													<Trans>All public invites are frozen. Raid mitigation mode is engaged.</Trans>
												) : (
													<Trans>
														Invites are active. Join velocity is monitored by automatic anti-raid triggers.
													</Trans>
												)}
											</span>
										</div>
									</div>
									<button
										type="button"
										className={isLockdown ? styles.panicLiftBtn : styles.panicTriggerBtn}
										onClick={handleTogglePanic}
										disabled={isTogglingLockdown}
										data-flx="guild.anti-raid-panic-modal.toggle-btn"
									>
										{isTogglingLockdown ? (
											<Spinner size="small" />
										) : isLockdown ? (
											<>
												<ShieldCheckIcon size={18} weight="bold" />
												<Trans>Lift Panic Lockdown</Trans>
											</>
										) : (
											<>
												<ProhibitIcon size={18} weight="bold" />
												<Trans>🚨 ACTIVATE PANIC LOCKDOWN</Trans>
											</>
										)}
									</button>
								</div>

								{/* Telemetry Stats Row */}
								<div className={styles.statsRow}>
									<div className={styles.statCard}>
										<span className={styles.statValue}>{recentJoins?.total_count ?? 0}</span>
										<span className={styles.statLabel}>
											<Trans>Joins in last</Trans> {withinMinutes}m
										</span>
									</div>
									<div className={styles.statCard}>
										<span className={clsx(styles.statValue, styles.statValueDanger)}>
											{recentJoins?.suspicious_count ?? 0}
										</span>
										<span className={styles.statLabel}>
											<Trans>Suspicious (&lt;24h old)</Trans>
										</span>
									</div>
									<div className={styles.statCard}>
										<span className={styles.statValue}>
											{status?.auto_velocity_trigger ? (
												<Trans>Active</Trans>
											) : (
												<Trans>Disabled</Trans>
											)}
										</span>
										<span className={styles.statLabel}>
											<Trans>Auto-Velocity Shield</Trans>
										</span>
									</div>
								</div>

								{/* Controls Bar */}
								<div className={styles.controlsBar}>
									<div className={styles.timePills}>
										{[15, 30, 60, 120].map((mins) => (
											<button
												key={mins}
												type="button"
												className={clsx(styles.pillBtn, withinMinutes === mins && styles.pillBtnActive)}
												onClick={() => handleTimeChange(mins)}
											>
												{mins}m
											</button>
										))}
									</div>

									<div className={styles.filterToggles}>
										<button
											type="button"
											className={clsx(styles.filterBtn, suspiciousOnly && styles.filterBtnActive)}
											onClick={() => setSuspiciousOnly(!suspiciousOnly)}
										>
											<WarningIcon size={14} weight="bold" />
											<Trans>Suspicious Accounts Only</Trans>
										</button>
										<button
											type="button"
											className={styles.filterBtn}
											onClick={() => void fetchAll()}
											title="Refresh"
										>
											<ArrowClockwiseIcon size={14} weight="bold" />
										</button>
									</div>
								</div>

								{/* Bulk Action Bar */}
								<div className={styles.bulkActionBar}>
									<div className={styles.bulkLeft}>
										<button
											type="button"
											className={styles.bulkSelectBtn}
											onClick={handleSelectAllSuspicious}
										>
											<Trans>Select Suspicious</Trans> ({recentJoins?.suspicious_count ?? 0})
										</button>
										<button type="button" className={styles.bulkSelectBtn} onClick={handleSelectAll}>
											<Trans>Select All</Trans> ({filteredMembers.length})
										</button>
										{selectedCount > 0 && (
											<button
												type="button"
												className={styles.bulkSelectBtn}
												onClick={handleClearSelection}
											>
												<Trans>Deselect</Trans>
											</button>
										)}

										<select
											className={styles.deleteMsgSelect}
											value={deleteMessageSeconds}
											onChange={(e) => setDeleteMessageSeconds(Number(e.target.value))}
										>
											<option value={0}>Purge: None</option>
											<option value={3600}>Purge: Past 1 Hour</option>
											<option value={86400}>Purge: Past 24 Hours</option>
											<option value={604800}>Purge: Past 7 Days</option>
										</select>
									</div>

									<div className={styles.bulkRight}>
										<button
											type="button"
											className={clsx(styles.bulkKickBtn, selectedCount === 0 && styles.bulkBtnDisabled)}
											disabled={selectedCount === 0 || isPruning}
											onClick={() => handleExecutePrune('kick')}
										>
											<UserMinusIcon size={15} weight="bold" />
											<span>
												<Trans>Batch Kick</Trans> ({selectedCount})
											</span>
										</button>
										<button
											type="button"
											className={clsx(styles.bulkBanBtn, selectedCount === 0 && styles.bulkBtnDisabled)}
											disabled={selectedCount === 0 || isPruning}
											onClick={() => handleExecutePrune('ban')}
										>
											<ProhibitIcon size={15} weight="bold" />
											<span>
												<Trans>Batch Ban</Trans> ({selectedCount})
											</span>
										</button>
									</div>
								</div>

								{/* Member List */}
								<div className={styles.memberListContainer}>
									{filteredMembers.length === 0 ? (
										<div className={styles.emptyState}>
											<ShieldCheckIcon size={40} weight="duotone" />
											<span className={styles.emptyTitle}>
												<Trans>No members match this window</Trans>
											</span>
											<span>
												<Trans>No members joined in this timeframe or none match the suspicious filter.</Trans>
											</span>
										</div>
									) : (
										filteredMembers.map((member: RecentJoinMember) => {
											const isSelected = selectedUserIds.has(member.user_id);
											const avatarUrl =
												member.avatar != null
													? getUserAvatarURL({id: member.user_id, avatar: member.avatar})
													: getDefaultAvatarURLForIndex(Number(BigInt(member.user_id) % 5n));

											return (
												<div
													key={member.user_id}
													className={clsx(
														styles.memberRow,
														member.is_suspicious && styles.memberRowSuspicious,
													)}
												>
													<div className={styles.memberRowLeft}>
														<Checkbox
															checked={isSelected}
															onChange={() => handleToggleMember(member.user_id)}
															size={18}
															noFocus
															aria-hidden={true}
														/>
														<BaseAvatar
															avatarUrl={avatarUrl}
															size={32}
														/>
														<div className={styles.memberInfo}>
															<div className={styles.memberNameGroup}>
																<span className={styles.memberName}>{member.username}</span>
																{member.discriminator > 0 && (
																	<span className={styles.memberDiscrim}>
																		#{member.discriminator.toString().padStart(4, '0')}
																	</span>
																)}
																{member.is_suspicious && (
																	<span className={styles.suspiciousBadge}>
																		<WarningCircleIcon size={12} weight="bold" />
																		<Trans>Suspicious</Trans> ({member.account_age_hours}h old)
																	</span>
																)}
															</div>
															<div className={styles.memberMeta}>
																<span>
																	<Trans>Joined</Trans> {member.joined_ago_minutes}m <Trans>ago</Trans>
																</span>
																<span>•</span>
																<span>
																	<Trans>Created</Trans> {member.account_age_hours}h <Trans>ago</Trans>
																</span>
																<span>•</span>
																<span>
																	{member.roles.length} <Trans>roles</Trans>
																</span>
															</div>
														</div>
													</div>

													<div className={styles.memberRowActions}>
														<button
															type="button"
															className={styles.actionIconBtn}
															title="Kick Raider"
															onClick={() => {
																setSelectedUserIds(new Set([member.user_id]));
																handleExecutePrune('kick');
															}}
														>
															<UserMinusIcon size={16} />
														</button>
														<button
															type="button"
															className={styles.actionIconBtn}
															title="Ban Raider"
															onClick={() => {
																setSelectedUserIds(new Set([member.user_id]));
																handleExecutePrune('ban');
															}}
														>
															<ProhibitIcon size={16} />
														</button>
													</div>
												</div>
											);
										})
									)}
								</div>
							</>
						)}
					</div>
				</Modal.ContentLayout>
			</Modal.Content>
		</Modal.Root>
	);
});

export default AntiRaidPanicModal;
